// src/services/apaleo/client.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import i18n from "../../i18n";
import type { FetchOptions } from './types';
import type { ApaleoClient } from './client-contract';
import { DEFAULT_CONFIG, errorMessage } from './config';

// --- TOKEN STATE ---
// Module-level `let`s, assigned by setToken/registerTokenGetter below and by
// refreshToken(). They stay in this module because an ESM binding can only be
// assigned by the module that declares it.
let inMemoryToken: string | null = null;
let tokenGetterFn: (() => Promise<string | null> | string | null) | null = null;
let refreshPromise: Promise<string | null> | null = null;
let lastRefreshFailTime = 0;
const REFRESH_BACKOFF_MS = 10_000;

async function refreshToken(): Promise<string | null> {
  // Backoff: if the last refresh failed recently, don't try again
  if (lastRefreshFailTime && Date.now() - lastRefreshFailTime < REFRESH_BACKOFF_MS) {
    return null;
  }
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async (): Promise<string | null> => {
    try {
      const headers: Record<string, string> = {};
      let tokenToUse: string | null = null;
      if (tokenGetterFn) {
        try {
          tokenToUse = await tokenGetterFn();
        } catch (e) {
          console.warn("[ApaleoAuth] Failed to get auth token:", e);
        }
      }
      if (tokenToUse) {
        headers["Authorization"] = `Bearer ${tokenToUse}`;
      }
      const refreshRes = await fetch("/api/token", { method: "POST", headers });
      if (!refreshRes.ok) {
        lastRefreshFailTime = Date.now();
        console.warn(`[ApaleoAuth] /api/token returned HTTP ${refreshRes.status}: ${refreshRes.statusText}`);
        return null;
      }
      const refreshData = (await refreshRes.json()) as { access_token?: string };
      if (refreshData.access_token) {
        inMemoryToken = refreshData.access_token;
        lastRefreshFailTime = 0; // reset backoff on success
        return refreshData.access_token;
      }
      lastRefreshFailTime = Date.now();
      return null;
    } catch (err) {
      console.error("[ApaleoAuth] Token refresh failed:", err);
      lastRefreshFailTime = Date.now();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

const part: ThisType<ApaleoClient> = {
registerTokenGetter(fn: () => Promise<string | null> | string | null) {
  tokenGetterFn = fn;
},

async refreshAccessToken() {
  return refreshToken();
},

getUserInfo() {
  const token = this.getToken();
  if (!token) return { name: i18n.t('common.info'), sub: "unknown" };
  try {
    const base64Url = token.split(".")[1];
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const payload = JSON.parse(jsonPayload);
    return {
      name:
        payload.name || payload.email || payload.client_id || "Apaleo User",
      sub: payload.sub || payload.account_code || "unknown",
    };
  } catch {
    return { name: i18n.t('common.info'), sub: "unknown" };
  }
},

getToken() {
  return inMemoryToken;
},

setToken(token: string | null) {
  inMemoryToken = token;
},

async _fetch(
  endpoint: string,
  options: FetchOptions = {},
  isRetry = false,
): Promise<unknown> {
  let token = this.getToken();
  if (!token && !isRetry) {
    console.log(
      "[ApaleoAuth] No in-memory token, attempting to fetch one...",
    );
    token = await refreshToken();
  }

  if (!token) return null;

  const url = endpoint.startsWith("http")
    ? endpoint
    : `${DEFAULT_CONFIG.apiUrl}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(!options.headers?.["Content-Type"] && {
        "Content-Type": "application/json",
      }),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    if (!isRetry) {
      console.log("Token expired, attempting silent refresh...");
      const newToken = await refreshToken();
      if (newToken) {
        console.log("Silent refresh successful.");
        return this._fetch(endpoint, options, true);
      }
    }

    this.setToken(null);
    console.error(
      "Session expired. Background task stopped to prevent interruption.",
    );
    // We no longer redirect to prevent data loss in active forms.
    // The next manual user action that requires API will naturally fail or prompt.
    throw new Error(i18n.t('common.error'));
  }

  if (options.raw) return response;
  if (response.status === 204) return true;
  if (!response.ok) {
    let details = "";
    try {
      const text = await response.text();
      details = text;
      try {
        const errData = JSON.parse(text) as {
          message?: unknown;
          error?: unknown;
        };
        details = errorMessage(errData.message) || errorMessage(errData.error) || text;
      } catch {
        // keep text details
      }
    } catch {
      details = "Response body unreadable";
    }
    throw new Error(`API Fehler ${response.status}: ${response.statusText}${details ? ` - ${details}` : ""}`);
  }
  return await response.json();
},

};

export default part;

export { inMemoryToken, tokenGetterFn, refreshToken };
